"use client";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./MediaInfo.module.scss";
import ModalRoot from "../_primitives/Modal/ModalRoot/ModalRoot";
import ModalTrigger from "../_primitives/Modal/ModalTrigger/ModalTrigger";
import ModalContent from "../_primitives/Modal/ModalContent/ModalContent";
import { ModalClose } from "../_primitives/Modal/ModalClose/ModalClose";
import ModalPortal from "../_primitives/Modal/ModalPortal/ModalPortal";
import ModalOverlay from "../_primitives/Modal/ModalOverlay/ModalOverlay";
import MediaInfoModalContent from "./MediaInfoModalContent";
import { generateCopyrightJSX } from "../Copyright/Copyright";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";
import type { MediaInfoProps } from "./MediaInfo";
import { computeMediaInfoFlags } from "./computeMediaInfoFlags";

const MediaInfoClient = ({
  media,
  video,
  image_desktop,
  image_fullhd,
  image_tablet,
  image_mobile,
  trigger,
  copyright,
  copyrightAriaLabel,
  ignoreImageMetadata = false,
  infoButtonPosition = "bottom",
}: MediaInfoProps) => {
  const { getIcon, label } = useGlobalContext();

  const flags = computeMediaInfoFlags({
    video,
    image_desktop,
    image_fullhd,
    image_tablet,
    image_mobile,
    copyright,
    ignoreImageMetadata,
  });

  const copyrightJSX = ignoreImageMetadata
    ? []
    : generateCopyrightJSX(image_desktop || undefined, label);

  return (
    <ModalRoot>
      <ModalTrigger>
        {trigger ?? (
          <button
            className={`${styles["media-info-button"]} button-reset media-info-button ${styles[infoButtonPosition]}`}
            aria-label={label("global", "aria_label", "media_info_button", {
              de: "Weitere Informationen anzeigen",
              en: "Show more information",
            })}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <CustomSVG
              src={getIcon("info")}
              wrapper={"span"}
              width={16}
              height={16}
              aria-hidden="true"
              className="is-flex"
            />
          </button>
        )}
      </ModalTrigger>
      <ModalPortal portalRef={undefined}>
        <ModalOverlay />
        <ModalContent>
          <ModalClose />
          <MediaInfoModalContent
            copyrightJSX={!flags.hasVideoCopyright ? copyrightJSX : []}
            copyrightAriaLabel={copyrightAriaLabel}
            media={media}
            image_desktop={image_desktop || undefined}
            image_fullhd={image_fullhd || undefined}
            image_tablet={image_tablet || undefined}
            image_mobile={image_mobile || undefined}
            hasVideoDescription={flags.hasVideoDescription}
            hasMobileDescription={flags.hasMobileDescription}
            hasTabletDescription={flags.hasTabletDescription}
            hasDesktopDescription={flags.hasDesktopDescription}
            hasFullhdDescription={flags.hasFullhdDescription}
            video={video}
            hasVideoCopyright={flags.hasVideoCopyright}
            copyright={copyright}
            descriptionLabel={label(
              "global",
              null,
              "media_description",
              "Beschreibung",
            )}
          />
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};

export default MediaInfoClient;
